import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  Plus, Search, MoreHorizontal, Edit, Trash2, Mail, Phone, Building2, Users,
  TrendingUp, Brain, AlertTriangle, Shield, Clock, FileText, Upload, Download,
  UserCheck, UserX, Timer, Briefcase, Award, BarChart3, Activity, ChevronRight,
  Zap, ArrowRight, Eye, Grid3x3, List, CalendarDays, UserCog, MapPin
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const ax = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const STATUS_MAP = { active: { label: "Activo", cls: "bg-emerald-100 text-emerald-700" }, inactive: { label: "Inactivo", cls: "bg-red-100 text-red-700" }, on_leave: { label: "Permiso", cls: "bg-amber-100 text-amber-700" } };

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [centerFilter, setCenterFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    try {
      const [empRes, statsRes] = await Promise.all([
        axios.get(`${API}/employees`, ax(token)),
        axios.get(`${API}/employees/stats`, ax(token)).catch(() => ({ data: null }))
      ]);
      setEmployees(empRes.data);
      setStats(statsRes.data);
    } catch { toast.error("Error al cargar empleados"); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Map employees by ID for supervisor lookup
  const empMap = useMemo(() => {
    const m = {};
    employees.forEach(e => { m[e.employee_id] = e; });
    return m;
  }, [employees]);

  const getSupervisorName = (supId) => {
    if (!supId || supId === "none") return null;
    const sup = empMap[supId];
    return sup ? `${sup.first_name} ${sup.last_name}` : null;
  };

  const loadAI = async () => {
    setAiLoading(true);
    try { const res = await axios.get(`${API}/employees/ai-insights`, ax(token)); setAiInsights(res.data); }
    catch { toast.error("Error al obtener insights IA"); } finally { setAiLoading(false); }
  };

  const loadTimeline = async (empId) => {
    try { const res = await axios.get(`${API}/employees/${empId}/timeline`, ax(token)); setTimeline(res.data); }
    catch { setTimeline([]); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este empleado?")) return;
    try { await axios.delete(`${API}/employees/${id}`, ax(token)); toast.success("Eliminado"); fetchData(); } catch { toast.error("Error"); }
  };

  const openDetail = (emp) => { setSelectedEmp(emp); setDetailOpen(true); loadTimeline(emp.employee_id); };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/employees/export/csv`, { ...ax(token), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "empleados.csv"; a.click();
    } catch { toast.error("Error al exportar"); }
  };

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const workCenters = [...new Set(employees.map(e => e.work_center_name).filter(Boolean))];
  const filtered = employees.filter(e => {
    const matchSearch = `${e.first_name} ${e.last_name} ${e.email} ${e.position} ${e.work_center_name || ""}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchDept = deptFilter === "all" || e.department === deptFilter;
    const matchCenter = centerFilter === "all" || e.work_center_name === centerFilter;
    const matchStatus = statusFilter === "all" || e.status === statusFilter;
    return matchSearch && matchDept && matchCenter && matchStatus;
  });

  if (loading) return <Layout title="Empleados"><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Gestion de Personas" subtitle={`${employees.length} colaboradores`}
      actions={<div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={handleExport} data-testid="export-btn"><Download className="w-4 h-4 mr-2" />Exportar</Button>
        <Link to="/employees/new"><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-2" />Agregar</Button></Link>
      </div>}>

      {/* KPI STRIP */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="employee-kpis">
            {[
            { label: "Total", value: stats.total, icon: Users, color: "bg-slate-100 text-slate-600" },
            { label: "Activos", value: stats.active, icon: UserCheck, color: "bg-emerald-100 text-emerald-600" },
            { label: "Nuevos (30d)", value: stats.new_hires_30d, icon: TrendingUp, color: "bg-blue-100 text-blue-600" },
              { label: "Departamentos", value: stats.dept_count, icon: Building2, color: "bg-violet-100 text-violet-600" },
              { label: "Centros", value: stats.center_count || 0, icon: MapPin, color: "bg-sky-100 text-sky-600" },
            { label: "Antiguedad Prom.", value: `${stats.avg_tenure_years}a`, icon: CalendarDays, color: "bg-amber-100 text-amber-600" },
              { label: "Salario Prom.", value: `$${(stats.avg_salary || 0).toLocaleString("es-MX")}`, icon: Briefcase, color: "bg-teal-100 text-teal-600" },
          ].map((s, i) => (
            <Card key={i} className="border-slate-200"><CardContent className="p-4"><div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
              <div><p className="text-xl font-bold text-slate-900">{s.value}</p><p className="text-[11px] text-slate-500">{s.label}</p></div>
            </div></CardContent></Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="directory" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="directory" data-testid="tab-directory">Directorio</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Dashboard</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai" className="text-violet-600"><Brain className="w-3.5 h-3.5 mr-1" />IA Insights</TabsTrigger>
        </TabsList>

        {/* === DIRECTORY === */}
        <TabsContent value="directory">
          <Card className="mb-4 border-slate-200">
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Buscar nombre, email, puesto..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-employees-input" />
              </div>
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger className="w-[180px]" data-testid="dept-filter"><SelectValue placeholder="Departamento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los deptos.</SelectItem>
                  {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={centerFilter} onValueChange={setCenterFilter}>
                <SelectTrigger className="w-[180px]" data-testid="center-filter"><SelectValue placeholder="Centro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los centros</SelectItem>
                  {workCenters.map(center => <SelectItem key={center} value={center}>{center}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Activos</SelectItem>
                  <SelectItem value="inactive">Inactivos</SelectItem>
                  <SelectItem value="on_leave">Permiso</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex border rounded-lg overflow-hidden">
                <button onClick={() => setViewMode("table")} className={`px-3 py-2 ${viewMode === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} data-testid="view-table"><List className="w-4 h-4" /></button>
                <button onClick={() => setViewMode("grid")} className={`px-3 py-2 ${viewMode === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} data-testid="view-grid"><Grid3x3 className="w-4 h-4" /></button>
              </div>
              <Badge variant="outline">{filtered.length} resultados</Badge>
            </CardContent>
          </Card>

          {filtered.length === 0 ? (
            <Card className="border-slate-200"><CardContent className="text-center py-16 text-slate-500"><Building2 className="w-14 h-14 mx-auto mb-3 text-slate-300" /><p className="font-medium mb-4">No hay empleados</p><Link to="/employees/new"><Button className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" />Agregar Primer Empleado</Button></Link></CardContent></Card>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map(emp => {
                const supName = getSupervisorName(emp.supervisor_id);
                return (
                  <Card key={emp.employee_id} className="border-slate-200 hover:shadow-lg transition-all cursor-pointer group" onClick={() => openDetail(emp)} data-testid={`emp-card-${emp.employee_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-sm group-hover:scale-110 transition-transform">
                          {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                        </div>
                        <Badge className={STATUS_MAP[emp.status]?.cls || "bg-slate-100 text-slate-600"} data-testid={`status-${emp.employee_id}`}>
                          {STATUS_MAP[emp.status]?.label || emp.status}
                        </Badge>
                      </div>
                      <h3 className="font-bold text-slate-900">{emp.first_name} {emp.last_name}</h3>
                      <p className="text-sm text-slate-500">{emp.position}</p>
                      <div className="flex items-center gap-1.5 mt-2">
                        <Building2 className="w-3 h-3 text-slate-400" /><span className="text-xs text-slate-500">{emp.department}</span>
                      </div>
                      {emp.work_center_name && <div className="flex items-center gap-1.5 mt-1"><MapPin className="w-3 h-3 text-sky-500" /><span className="text-xs text-sky-600 font-medium">{emp.work_center_name}</span></div>}
                      <div className="flex items-center gap-1.5 mt-1">
                        <Mail className="w-3 h-3 text-slate-400" /><span className="text-xs text-slate-500 truncate">{emp.email}</span>
                      </div>
                      {supName && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <UserCog className="w-3 h-3 text-emerald-500" /><span className="text-xs text-emerald-600 font-medium">Reporta a: {supName}</span>
                        </div>
                      )}
                      {emp.hire_date && <div className="flex items-center gap-1.5 mt-1"><CalendarDays className="w-3 h-3 text-slate-400" /><span className="text-xs text-slate-500">Desde {new Date(emp.hire_date).toLocaleDateString("es-MX")}</span></div>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table className="data-table">
                  <TableHeader><TableRow>
                    <TableHead>Empleado</TableHead><TableHead>Puesto</TableHead><TableHead>Departamento</TableHead><TableHead>Centro</TableHead><TableHead>Jefe Inmediato</TableHead><TableHead>Contacto</TableHead><TableHead>Estado</TableHead><TableHead className="w-[80px]"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{filtered.map(emp => (
                    <TableRow key={emp.employee_id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(emp)} data-testid={`emp-row-${emp.employee_id}`}>
                      <TableCell><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-sm font-semibold">{emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}</div><div><p className="font-medium text-slate-900">{emp.first_name} {emp.last_name}</p></div></div></TableCell>
                      <TableCell className="text-sm">{emp.position}</TableCell>
                      <TableCell><Badge variant="outline" className="font-normal text-xs">{emp.department}</Badge></TableCell>
                      <TableCell>{emp.work_center_name ? <span className="text-xs text-sky-600 font-medium flex items-center gap-1"><MapPin className="w-3 h-3" />{emp.work_center_name}</span> : <span className="text-xs text-slate-400">-</span>}</TableCell>
                      <TableCell>{getSupervisorName(emp.supervisor_id) ? <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><UserCog className="w-3 h-3" />{getSupervisorName(emp.supervisor_id)}</span> : <span className="text-xs text-slate-400">-</span>}</TableCell>
                      <TableCell><div className="text-xs text-slate-500"><div className="flex items-center gap-1"><Mail className="w-3 h-3" />{emp.email}</div></div></TableCell>
                      <TableCell><Badge className={STATUS_MAP[emp.status]?.cls || "bg-slate-100 text-slate-600"}>{STATUS_MAP[emp.status]?.label || emp.status}</Badge></TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`emp-actions-${emp.employee_id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(emp)}><Eye className="w-4 h-4 mr-2" />Ver Perfil</DropdownMenuItem>
                            <DropdownMenuItem asChild><Link to={`/employees/${emp.employee_id}/edit`} className="flex items-center gap-2"><Edit className="w-4 h-4" />Editar</Link></DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(emp.employee_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                          </DropdownMenuContent></DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* === ANALYTICS DASHBOARD === */}
        <TabsContent value="analytics">
          {stats ? (
            <div className="space-y-4">
              <Card className="border-slate-200"><CardHeader><CardTitle className="text-base">Distribucion por Departamento</CardTitle></CardHeader>
                <CardContent><div className="space-y-3">
                  {stats.departments.map((d, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-700 w-32 truncate">{d.name}</span>
                      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${Math.max(d.percentage, 8)}%` }}>
                          <span className="text-[10px] text-white font-bold">{d.count}</span>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400 w-12 text-right">{d.percentage}%</span>
                    </div>
                  ))}
                </div></CardContent>
              </Card>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-slate-200"><CardHeader><CardTitle className="text-base">Estado de Empleados</CardTitle></CardHeader>
                  <CardContent><div className="flex items-center gap-4">
                    {[
                      { label: "Activos", value: stats.active, color: "bg-emerald-500", pct: stats.total ? Math.round(stats.active / stats.total * 100) : 0 },
                      { label: "Inactivos", value: stats.inactive, color: "bg-red-500", pct: stats.total ? Math.round(stats.inactive / stats.total * 100) : 0 },
                      { label: "Permiso", value: stats.on_leave, color: "bg-amber-500", pct: stats.total ? Math.round(stats.on_leave / stats.total * 100) : 0 },
                    ].map((s, i) => (
                      <div key={i} className="flex-1 text-center">
                        <div className={`w-full h-3 ${s.color} rounded-full mb-2 opacity-80`} style={{ width: `${Math.max(s.pct, 5)}%`, margin: "0 auto" }} />
                        <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                        <p className="text-xs text-slate-500">{s.label} ({s.pct}%)</p>
                      </div>
                    ))}
                  </div></CardContent>
                </Card>
                <Card className="border-slate-200"><CardHeader><CardTitle className="text-base">Informacion General</CardTitle></CardHeader>
                  <CardContent><div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-xl bg-blue-50 text-center"><p className="text-xs text-blue-500 font-semibold">Antiguedad Promedio</p><p className="text-xl font-bold text-blue-700">{stats.avg_tenure_years} años</p></div>
                    <div className="p-3 rounded-xl bg-violet-50 text-center"><p className="text-xs text-violet-500 font-semibold">Salario Promedio</p><p className="text-xl font-bold text-violet-700">${(stats.avg_salary || 0).toLocaleString("es-MX")}</p></div>
                    <div className="p-3 rounded-xl bg-emerald-50 text-center"><p className="text-xs text-emerald-500 font-semibold">Nuevos (30 dias)</p><p className="text-xl font-bold text-emerald-700">{stats.new_hires_30d}</p></div>
                    <div className="p-3 rounded-xl bg-amber-50 text-center"><p className="text-xs text-amber-500 font-semibold">Departamentos</p><p className="text-xl font-bold text-amber-700">{stats.dept_count}</p></div>
                  </div></CardContent>
                </Card>
              </div>
            </div>
          ) : <div className="text-center py-12 text-slate-500">Cargando estadisticas...</div>}
        </TabsContent>

        {/* === AI INSIGHTS === */}
        <TabsContent value="ai">
          <Card className="border-slate-200 mb-4">
            <CardContent className="p-4 flex items-center justify-between">
              <div><h3 className="font-semibold text-slate-900 flex items-center gap-2"><Brain className="w-5 h-5 text-violet-500" />Analisis de Personas con IA</h3><p className="text-xs text-slate-500 mt-1">Prediccion de riesgo de rotacion, salud departamental y recomendaciones</p></div>
              <Button onClick={loadAI} disabled={aiLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="load-employee-ai-btn">
                {aiLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}Analizar
              </Button>
            </CardContent>
          </Card>
          {aiInsights && (
            <div className="space-y-4">
              {aiInsights.insights.map((ins, i) => (
                <Card key={i} className={`border-l-4 ${ins.severity === "high" ? "border-l-red-500" : ins.severity === "medium" ? "border-l-amber-500" : "border-l-blue-500"}`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${ins.severity === "high" ? "bg-red-100 text-red-600" : ins.severity === "medium" ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"}`}>
                      {ins.type === "critical" ? <AlertTriangle className="w-4 h-4" /> : ins.type === "training" ? <Award className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
                    </div>
                    <div className="flex-1"><p className="font-semibold text-sm text-slate-900">{ins.message}</p><p className="text-xs text-slate-500 mt-1"><ArrowRight className="w-3 h-3 inline mr-1" />{ins.recommendation}</p></div>
                  </CardContent>
                </Card>
              ))}
              {aiInsights.risk_employees.length > 0 && (
                <Card className="border-slate-200"><CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Empleados con Riesgo de Rotacion</CardTitle></CardHeader>
                  <CardContent>
                    <Table className="data-table"><TableHeader><TableRow><TableHead>Empleado</TableHead><TableHead>Depto</TableHead><TableHead>Riesgo</TableHead><TableHead>Factores</TableHead></TableRow></TableHeader>
                      <TableBody>{aiInsights.risk_employees.map((r, i) => (
                        <TableRow key={i} data-testid={`risk-emp-${i}`}>
                          <TableCell className="font-medium">{r.name}<br /><span className="text-xs text-slate-500">{r.position}</span></TableCell>
                          <TableCell>{r.department}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 rounded-full bg-slate-100"><div className={`h-full rounded-full ${r.risk_level === "high" ? "bg-red-500" : r.risk_level === "medium" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${r.risk_score}%` }} /></div>
                              <Badge className={r.risk_level === "high" ? "bg-red-100 text-red-700" : r.risk_level === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>{r.risk_score}%</Badge>
                            </div>
                          </TableCell>
                          <TableCell><ul className="text-xs text-slate-500 space-y-0.5">{r.factors.map((f, j) => <li key={j}>- {f}</li>)}</ul></TableCell>
                        </TableRow>
                      ))}</TableBody></Table>
                  </CardContent>
                </Card>
              )}
              {aiInsights.department_health.length > 0 && (
                <Card className="border-slate-200"><CardHeader><CardTitle className="text-base">Salud Departamental</CardTitle></CardHeader>
                  <CardContent><div className="grid md:grid-cols-2 gap-3">
                    {aiInsights.department_health.map((d, i) => (
                      <div key={i} className="p-4 rounded-xl border border-slate-200 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.risk_pct >= 30 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}><Building2 className="w-5 h-5" /></div>
                        <div className="flex-1"><p className="font-semibold text-sm">{d.department}</p><p className="text-xs text-slate-500">{d.at_risk} de {d.total} en riesgo ({d.risk_pct}%)</p></div>
                      </div>
                    ))}
                  </div></CardContent>
                </Card>
              )}
              <p className="text-xs text-slate-400 text-center">Analisis basado en {aiInsights.total_analyzed} empleados activos</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* === EMPLOYEE DETAIL DIALOG === */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedEmp && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xl font-bold shadow-md">
                    {selectedEmp.first_name?.charAt(0)}{selectedEmp.last_name?.charAt(0)}
                  </div>
                  <div>
                    <DialogTitle className="text-xl">{selectedEmp.first_name} {selectedEmp.last_name}</DialogTitle>
                    <p className="text-sm text-slate-500">{selectedEmp.position} - {selectedEmp.department}</p>
                    <Badge className={STATUS_MAP[selectedEmp.status]?.cls || "bg-slate-100"}>{STATUS_MAP[selectedEmp.status]?.label || selectedEmp.status}</Badge>
                  </div>
                </div>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-4">
                {[
                  { icon: Mail, label: "Email", value: selectedEmp.email },
                  { icon: Phone, label: "Telefono", value: selectedEmp.phone || "-" },
                  { icon: CalendarDays, label: "Fecha Ingreso", value: selectedEmp.hire_date ? new Date(selectedEmp.hire_date).toLocaleDateString("es-MX") : "-" },
                  { icon: Briefcase, label: "Salario", value: selectedEmp.salary ? `$${selectedEmp.salary.toLocaleString("es-MX")}` : "-" },
                  { icon: MapPin, label: "Centro", value: selectedEmp.work_center_name || "-" },
                  { icon: FileText, label: "RFC", value: selectedEmp.rfc || "-" },
                  { icon: Shield, label: "CURP", value: selectedEmp.curp || "-" },
                  { icon: FileText, label: "NSS", value: selectedEmp.nss || "-" },
                  { icon: Users, label: "Genero", value: selectedEmp.gender || "-" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                    <f.icon className="w-4 h-4 text-slate-400 shrink-0" />
                    <div><p className="text-[10px] text-slate-400 uppercase">{f.label}</p><p className="text-sm font-medium text-slate-700">{f.value}</p></div>
                  </div>
                ))}
              </div>
              {/* Supervisor info */}
              {getSupervisorName(selectedEmp.supervisor_id) && (
                <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
                  <UserCog className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-[10px] text-emerald-500 uppercase font-semibold">Jefe Inmediato</p>
                    <p className="text-sm font-bold text-emerald-700">{getSupervisorName(selectedEmp.supervisor_id)}</p>
                  </div>
                </div>
              )}
              {/* Timeline */}
              <div className="mt-6">
                <h4 className="font-semibold text-sm text-slate-700 mb-3 flex items-center gap-2"><Activity className="w-4 h-4" />Linea de Tiempo</h4>
                {timeline.length === 0 ? <p className="text-xs text-slate-400">Sin actividad registrada</p> : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {timeline.map((ev, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 rounded-lg border border-slate-100 text-xs">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${ev.type === "change" ? "bg-blue-100 text-blue-600" : ev.type === "leave" ? "bg-amber-100 text-amber-600" : "bg-violet-100 text-violet-600"}`}>
                          {ev.type === "change" ? <Edit className="w-3 h-3" /> : ev.type === "leave" ? <Clock className="w-3 h-3" /> : <Award className="w-3 h-3" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-700">{ev.type === "change" ? `Cambio en: ${ev.fields?.join(", ")}` : ev.type === "leave" ? `Permiso ${ev.details?.leave_type || ""} (${ev.status})` : `Capacitacion (${ev.status})`}</p>
                          <p className="text-slate-400">{ev.date ? new Date(ev.date).toLocaleDateString("es-MX") : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter className="mt-4">
                <Link to={`/employees/${selectedEmp.employee_id}/edit`}><Button className="bg-emerald-600 hover:bg-emerald-700"><Edit className="w-4 h-4 mr-2" />Editar</Button></Link>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Employees;
